import { useState } from "react";
import {
  View,
  Text,
  Pressable,
  TextInput,
  Image,
  Platform,
} from "react-native";
import { ChevronRight } from "lucide-react-native";
import { ChatwootSheet } from "@louise/chatwoot";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useNotificationDeliverability } from "@src/packages/notifications";
import { useRequestAccountDeactivation } from "@src/packages/profile/hooks/useRequestAccountDeactivation";
import { EMBER } from "../../../../onboarding/theme";
import { useStytch } from "../../../../utils/useStytch";
import {
  Dialog,
  DialogPrimaryButton,
  SoftSecondaryButton,
} from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";

const trashIcon =
  require("../../../../assets/images/you-icons/trash-bin.png") as number;
const alertIcon =
  require("../../../../assets/images/you-icons/alert.png") as number;
const envelopeIcon =
  require("../../../../assets/images/you-icons/envelope.png") as number;

type Props = {
  visible: boolean;
  confirmText: string;
  onChangeText: (text: string) => void;
  onClose: () => void;
};

export default function DeleteAccountModal({
  visible,
  confirmText,
  onChangeText,
  onClose,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("DeleteAccountModal");
  const stytch = useStytch();
  const { revokeRegisteredDevice } = useNotificationDeliverability();
  const requestDeactivation = useRequestAccountDeactivation();
  const confirmationToken = t("you.modals.delete_account.confirmation_token");
  const isEnabled = confirmText === confirmationToken;
  const [supportVisible, setSupportVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      await revokeRegisteredDevice();
    } catch {
      // Account deactivation remains available if device cleanup is offline;
      // the backend account lifecycle also removes its device registrations.
    }
    try {
      await requestDeactivation.mutateAsync();
    } catch {
      setError(t("you.modals.delete_account.error"));
      return;
    }
    try {
      // Sign out locally. The backend has disabled account access, and
      // `forceClear` ensures the local user/session is removed even if the
      // revoke network call cannot complete. RootNavigator then replaces the
      // authenticated Profile stack with its unauthenticated stack, whose
      // initial route is Onboarding. Resetting to Onboarding before this revoke
      // races WelcomeGoals' authenticated-user guard, which redirects to Home.
      await stytch.session.revoke({ forceClear: true });
    } catch {
      // no-op: account access was already deactivated server-side
    }
  };

  return (
    <>
      <Dialog
        visible={visible}
        onClose={onClose}
        layout="centered"
        contentStyle={{ paddingTop: 28, paddingBottom: 24 }}
      >
        <Image
          testID="delete-account-trash-icon"
          source={trashIcon}
          style={{ width: 64, height: 72, alignSelf: "center" }}
          resizeMode="contain"
        />
        <Text
          accessibilityRole="header"
          className="font-dm-sans"
          style={{
            fontSize: 20,
            fontWeight: "700",
            color: EMBER.colors.text,
            textAlign: "center",
            marginTop: 12,
            marginBottom: 8,
          }}
        >
          {t("you.modals.delete_account.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            textAlign: "center",
            lineHeight: 20,
            marginBottom: 14,
          }}
        >
          {t("you.modals.delete_account.message")}
        </Text>

        {/* Warning callout — compact because the lead copy already gives the
          account-level consequence. */}
        <View
          style={{
            ...GLASS_CARD_STYLE,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            padding: 12,
            marginBottom: 10,
          }}
        >
          <Image
            source={alertIcon}
            style={{
              width: 36,
              height: 36,
              ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
            }}
            resizeMode="contain"
          />
          <View style={{ flex: 1, gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.text,
                lineHeight: 17,
              }}
            >
              {t("you.modals.delete_account.deactivation_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                color: EMBER.colors.textLight,
                lineHeight: 15,
              }}
            >
              {t("you.modals.delete_account.deactivation_description")}
            </Text>
          </View>
        </View>

        {/* Soft escape hatch — visually secondary to the confirmation flow. */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("you.modals.delete_account.reactivation_title")),
              "you.modals.delete_account.reactivation_title",
            );
            setSupportVisible(true);
          }}
          style={{
            ...GLASS_CARD_STYLE,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            padding: 12,
            marginBottom: 14,
          }}
        >
          <Image
            source={envelopeIcon}
            style={{
              width: 36,
              height: 36,
              ...(Platform.OS === "web" && { mixBlendMode: "overlay" }),
            }}
            resizeMode="contain"
          />
          <View style={{ flex: 1, gap: 2 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.text,
                lineHeight: 17,
              }}
            >
              {t("you.modals.delete_account.reactivation_title")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11.5,
                color: EMBER.colors.textLight,
                lineHeight: 15,
              }}
            >
              {t("you.modals.delete_account.reactivation_description")}
            </Text>
          </View>
          <ChevronRight
            size={16}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
        </Pressable>

        {/* Explicitly name the account-state change: this is not a permanent
          data deletion. */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: EMBER.colors.textLight,
            marginBottom: 8,
          }}
        >
          {t("you.modals.delete_account.type_prefix")}{" "}
          <Text style={{ fontWeight: "700", color: EMBER.colors.text }}>
            {confirmationToken}
          </Text>{" "}
          {t("you.modals.delete_account.type_suffix")}
        </Text>
        <TextInput
          value={confirmText}
          onChangeText={onChangeText}
          placeholder={t("you.modals.delete_account.type_placeholder")}
          placeholderTextColor={EMBER.colors.textFaint}
          autoCapitalize="characters"
          autoCorrect={false}
          className="font-dm-sans"
          style={{
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: EMBER.colors.border,
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            fontSize: 15,
            color: EMBER.colors.text,
            marginBottom: 16,
          }}
        />

        {/* The longer explicit action gets enough room to remain readable at
          the compact dialog size. */}
        <View
          testID="delete-account-actions"
          style={{ flexDirection: "row", gap: 12 }}
        >
          <SoftSecondaryButton
            label={t("you.modals.delete_account.cancel")}
            onPress={onClose}
            flex={1}
          />
          <DialogPrimaryButton
            label={t("you.modals.delete_account.confirm")}
            onPress={handleConfirm}
            disabled={!isEnabled || requestDeactivation.isPending}
            flex={1.5}
          />
        </View>
        {error ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.coral,
              textAlign: "center",
              marginTop: 12,
            }}
          >
            {error}
          </Text>
        ) : null}
      </Dialog>
      <ChatwootSheet
        visible={supportVisible}
        onClose={() => setSupportVisible(false)}
      />
    </>
  );
}
